import type {NewTeachersAssignScope} from 'common/components/dataUpload/NewTeachersModal'
import type {
  DataUploadNewStudent,
  DataUploadNewTeacher,
  DataUploadTeacherMatch,
} from 'common/helpers/dataUpload'
import {GraduationCap, UserPlus} from 'lucide-react'
import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'

/** Students listed in the dialog; the rest are counted ("…and 40 more"). */
const MAX_LISTED_STUDENTS = 100

/** What happens to the file's new teachers. */
export type NewTeachersChoice = 'assign' | 'only' | 'none'
/** What happens to the file's new students. */
export type NewStudentsChoice = 'only' | 'choose' | 'none'

export interface NewPeopleAnswer {
  teachers: NewTeachersChoice
  /** The teachers to add (`approvedNewTeachers`), in the server's order. */
  teacherKeys: string[]
  /** Unknown names matched to a saved teacher (`teacherMatches`); [] without `matchable`. */
  teacherMatches: DataUploadTeacherMatch[]
  students: NewStudentsChoice
}

/** One unknown teacher's answer: the saved teacher it is, a new teacher, or not now. */
type TeacherDecision = {kind: 'match'; teacherId: number} | {kind: 'add'} | {kind: 'skip'}

/** Why a saved teacher is suggested (`suggestions[].why`), as the dialog says it. */
const WHY_KEYS: Record<string, string> = {
  same_employee_id: 'DATA_UPLOAD.NEW_TEACHERS.WHY_SAME_EMPLOYEE_ID',
  spelling: 'DATA_UPLOAD.NEW_TEACHERS.WHY_SPELLING',
  extra_word: 'DATA_UPLOAD.NEW_TEACHERS.WHY_EXTRA_WORD',
  initials: 'DATA_UPLOAD.NEW_TEACHERS.WHY_INITIALS',
  word_order: 'DATA_UPLOAD.NEW_TEACHERS.WHY_WORD_ORDER',
  spacing: 'DATA_UPLOAD.NEW_TEACHERS.WHY_SPACING',
}

const ASSIGN_LABELS = {
  lines: 'DATA_UPLOAD.NEW_TEACHERS.SAVE_ASSIGN_LINES',
  classes: 'DATA_UPLOAD.NEW_TEACHERS.SAVE_ASSIGN_CLASSES',
  everything: 'DATA_UPLOAD.NEW_TEACHERS.SAVE_ASSIGN_ALL',
} satisfies Record<NewTeachersAssignScope, string>

/**
 * The lines worth listing under a teacher: each once, and not one that only says the teacher's own
 * name or employee id (a file with a name column and nothing else read every line as
 * "Adeel Rehman"). With nothing left, the "18 lines" count says it all.
 */
const linesWorthListing = (
  teacher: Pick<DataUploadNewTeacher, 'name' | 'employeeId'>,
  lines: readonly string[]
): string[] => {
  const own = new Set(
    [teacher.name, teacher.employeeId].map((value) => String(value ?? '').trim().toLowerCase())
  )
  const seen = new Set<string>()
  return lines.filter((line) => {
    const key = String(line ?? '').trim().toLowerCase()
    if (!key || own.has(key) || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

interface Props {
  show: boolean
  teachers: readonly DataUploadNewTeacher[]
  /** The rest of the file imports without the teachers' lines ("Don't save" leaves them out). */
  canContinueWithout: boolean
  /** What "Save and assign" gives the teachers (its label). */
  assignScope: NewTeachersAssignScope
  students: readonly DataUploadNewStudent[]
  /**
   * Offer "Match to existing" for teachers with `suggestions` (the caller re-sends with
   * `teacherMatches`). Left out, each teacher is only added or skipped.
   */
  matchable?: boolean
  /** The lines held back for each teacher (by `key`, from `report.skipped[].newTeacherKey`). */
  heldLines?: Readonly<Record<string, readonly string[]>>
  /** The file is only a students list: its students are all it holds for this step. */
  studentsOnly?: boolean
  submitting?: boolean
  onConfirm: (answer: NewPeopleAnswer) => void
  /** The footer's "Don't save": nobody is saved (the X too, when there is no `onClose`). */
  onCancel: () => void
  /** The X (and Esc / backdrop): closes without answering; the question stays pending. */
  onClose?: () => void
}

/**
 * The people an upload names that the school has not saved, in one dialog: its teachers
 * (allocation lines wait on them) and its students (another list in the same workbook). Each
 * group has its own choice — teachers: save and assign (default), save only, or not; students:
 * save only (default), save and choose their classes and subjects on the Students step, or not —
 * and one Continue applies both.
 */
const NewPeopleModal: FC<Props> = ({
  show,
  teachers,
  canContinueWithout,
  assignScope,
  students,
  matchable = false,
  heldLines,
  studentsOnly = false,
  submitting = false,
  onConfirm,
  onCancel,
  onClose,
}) => {
  const intl = useIntl()
  /** Each teacher's answer, by `key`. Nothing is matched automatically: all start as "add". */
  const [decisions, setDecisions] = useState<Record<string, TeacherDecision>>({})
  const [teacherChoice, setTeacherChoice] = useState<NewTeachersChoice>('assign')
  const [studentChoice, setStudentChoice] = useState<NewStudentsChoice>('only')

  // Each time it opens: every teacher to add, and the default choices.
  useEffect(() => {
    if (!show) return
    setDecisions(Object.fromEntries(teachers.map((teacher) => [teacher.key, {kind: 'add'}])))
    setTeacherChoice('assign')
    setStudentChoice('only')
  }, [show, teachers])

  const decisionOf = (key: string): TeacherDecision => decisions[key] ?? {kind: 'add'}
  const decide = (key: string, decision: TeacherDecision) =>
    setDecisions((prev) => ({...prev, [key]: decision}))
  /** The teachers to add, in the server's order. */
  const picked = teachers.filter((teacher) => decisionOf(teacher.key).kind === 'add')
  const matches: DataUploadTeacherMatch[] = matchable
    ? teachers.flatMap((teacher) => {
        const decision = decisionOf(teacher.key)
        return decision.kind === 'match' ? [{name: teacher.name, teacherId: decision.teacherId}] : []
      })
    : []

  const hasTeachers = teachers.length > 0
  const hasStudents = students.length > 0
  /** Some teacher is to be added: the "save and assign / save only" choice applies to them. */
  const savesTeachers = hasTeachers && picked.length > 0
  const listed = students.slice(0, MAX_LISTED_STUDENTS)
  const more = students.length - listed.length

  /** The choice buttons of one group. */
  const choices = <T extends string>(
    options: Array<{value: T; label: string}>,
    value: T,
    onChange: (next: T) => void
  ) => (
    <div className='d-flex flex-wrap gap-2 mb-2' role='radiogroup'>
      {options.map((option) => (
        <button
          key={option.value}
          type='button'
          role='radio'
          aria-checked={value === option.value}
          disabled={submitting}
          className={`btn btn-sm ${value === option.value ? 'btn-primary' : 'btn-light'}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : (onClose ?? onCancel)}
      centered
      size='lg'
      backdrop={submitting ? 'static' : undefined}
    >
      <Modal.Header closeButton={!submitting} className='border-0 pb-0'>
        <Modal.Title className='fs-3'>
          {intl.formatMessage({
            id:
              hasTeachers && hasStudents
                ? 'DATA_UPLOAD.NEW_PEOPLE.TITLE'
                : hasTeachers
                  ? 'DATA_UPLOAD.NEW_TEACHERS.TITLE'
                  : 'DATA_UPLOAD.NEW_PEOPLE.STUDENTS_TITLE',
          })}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className='pt-4' style={{maxHeight: '65vh', overflowY: 'auto'}}>
        {hasTeachers && (
          <section className='mb-6'>
            <div className='d-flex align-items-center gap-2 fw-bold fs-5'>
              <UserPlus size={18} className='text-primary' aria-hidden />
              {intl.formatMessage(
                {id: 'DATA_UPLOAD.NEW_PEOPLE.TEACHERS_FOUND'},
                {count: teachers.length}
              )}
            </div>
            <p className='sheet-assign-hint mt-1 mb-3'>
              {intl.formatMessage({
                id: canContinueWithout
                  ? 'DATA_UPLOAD.NEW_TEACHERS.HINT_SOME'
                  : 'DATA_UPLOAD.NEW_TEACHERS.HINT_ALL',
              })}
            </p>
            {/* Each teacher: the saved teacher it probably is, a new teacher, or not now. */}
            {teachers.map((teacher) => {
              const decision = decisionOf(teacher.key)
              const canMatch = matchable && teacher.suggestions.length > 0
              const held = linesWorthListing(teacher, heldLines?.[teacher.key] ?? [])
              const samples = linesWorthListing(teacher, teacher.samples)
              const option = (kind: TeacherDecision['kind'], label: string) => (
                <button
                  type='button'
                  role='radio'
                  aria-checked={decision.kind === kind}
                  disabled={submitting}
                  className={`btn btn-sm ${decision.kind === kind ? 'btn-primary' : 'btn-light'}`}
                  onClick={() =>
                    decide(
                      teacher.key,
                      kind === 'match'
                        ? {kind: 'match', teacherId: teacher.suggestions[0].teacherId}
                        : {kind}
                    )
                  }
                >
                  {label}
                </button>
              )
              return (
                <div
                  key={teacher.key}
                  className={`sheet-assign-card${decision.kind !== 'skip' ? ' is-answered' : ''}`}
                >
                  <span className='d-flex flex-wrap align-items-center gap-2'>
                    <span className='sheet-assign-name'>{teacher.name}</span>
                    {teacher.employeeId && (
                      <span className='badge badge-light fs-8 fw-semibold'>
                        {teacher.employeeId}
                      </span>
                    )}
                    {teacher.lines > 0 && (
                      <span className='badge badge-light-primary fs-8 fw-semibold'>
                        {intl.formatMessage(
                          {id: 'DATA_UPLOAD.NEW_TEACHERS.LINES'},
                          {count: teacher.lines}
                        )}
                      </span>
                    )}
                  </span>
                  {held.length > 0 ? (
                    // The lines held back for this teacher (`newTeacherKey`), under their name.
                    <span className='d-block text-muted fs-7 mt-1'>{held.join(' · ')}</span>
                  ) : samples.length > 0 ? (
                    <span className='d-block text-muted fs-7 mt-1'>{samples.join(' · ')}</span>
                  ) : null}
                  <div
                    className='d-flex flex-wrap gap-2 mt-3'
                    role='radiogroup'
                    aria-label={teacher.name}
                  >
                    {canMatch &&
                      option('match', intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.MATCH'}))}
                    {option('add', intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.ADD_NEW'}))}
                    {option('skip', intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.SKIP'}))}
                  </div>
                  {canMatch && decision.kind === 'match' && (
                    <div className='mt-3' role='radiogroup'>
                      {teacher.suggestions.map((suggestion) => {
                        const inputId = `new-person-match-${teacher.key}-${suggestion.teacherId}`
                        return (
                          <label
                            key={suggestion.teacherId}
                            htmlFor={inputId}
                            className='d-flex flex-wrap align-items-center gap-2 mb-2'
                          >
                            <input
                              id={inputId}
                              type='radio'
                              className='form-check-input flex-shrink-0 mt-0'
                              name={`new-person-match-${teacher.key}`}
                              checked={decision.teacherId === suggestion.teacherId}
                              disabled={submitting}
                              onChange={() =>
                                decide(teacher.key, {
                                  kind: 'match',
                                  teacherId: suggestion.teacherId,
                                })
                              }
                            />
                            <span className='sheet-assign-name'>{suggestion.name}</span>
                            {suggestion.employeeId && (
                              <span className='badge badge-light fs-8 fw-semibold'>
                                {suggestion.employeeId}
                              </span>
                            )}
                            {suggestion.why && WHY_KEYS[suggestion.why] && (
                              <span className='text-muted fs-8'>
                                {intl.formatMessage({id: WHY_KEYS[suggestion.why]})}
                              </span>
                            )}
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
            {/* What "add" gives the teachers being added: their lines too, or the name only. */}
            {savesTeachers && (
              <div className='mt-4'>
                {choices<NewTeachersChoice>(
                  [
                    {value: 'assign', label: intl.formatMessage({id: ASSIGN_LABELS[assignScope]})},
                    {
                      value: 'only',
                      label: intl.formatMessage(
                        {id: 'DATA_UPLOAD.NEW_TEACHERS.SAVE_ONLY'},
                        {count: picked.length}
                      ),
                    },
                  ],
                  teacherChoice === 'none' ? 'assign' : teacherChoice,
                  setTeacherChoice
                )}
                <p className='text-muted fs-8 mb-0'>
                  {intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.OPTIONS_NOTE_LINES'})}
                </p>
              </div>
            )}
          </section>
        )}

        {hasStudents && (
          <section>
            <div className='d-flex align-items-center gap-2 fw-bold fs-5'>
              <GraduationCap size={18} className='text-primary' aria-hidden />
              {intl.formatMessage({id: 'DATA_UPLOAD.NEW_STUDENTS.TITLE'}, {count: students.length})}
            </div>
            <p className='sheet-assign-hint mt-1 mb-3'>
              {intl.formatMessage({
                id: studentsOnly
                  ? 'DATA_UPLOAD.NEW_STUDENTS.HINT'
                  : 'DATA_UPLOAD.NEW_STUDENTS.HINT_ALSO',
              })}
            </p>
            {choices<NewStudentsChoice>(
              [
                {
                  value: 'only',
                  label: intl.formatMessage({id: 'DATA_UPLOAD.NEW_STUDENTS.SAVE_ONLY'}),
                },
                {
                  value: 'choose',
                  label: intl.formatMessage({id: 'DATA_UPLOAD.NEW_STUDENTS.SAVE_CHOOSE'}),
                },
                {
                  value: 'none',
                  label: intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.DONT_SAVE'}),
                },
              ],
              studentChoice,
              setStudentChoice
            )}
            {studentChoice !== 'none' && (
              <p className='text-muted fs-8 mb-3'>
                {intl.formatMessage({
                  id:
                    studentChoice === 'only'
                      ? 'DATA_UPLOAD.NEW_STUDENTS.ONLY_HINT'
                      : 'DATA_UPLOAD.NEW_STUDENTS.CHOOSE_HINT',
                })}
              </p>
            )}
            {studentChoice !== 'none' && (
              <>
                {listed.map((student, index) => (
                  <div
                    key={`${student.registrationNumber ?? ''}-${student.name}-${index}`}
                    className='sheet-assign-card'
                  >
                    <span className='d-flex flex-wrap align-items-center gap-2'>
                      <span className='sheet-assign-name'>{student.name}</span>
                      {student.registrationNumber && (
                        <span className='badge badge-light fs-8 fw-semibold'>
                          {student.registrationNumber}
                        </span>
                      )}
                      {student.class && (
                        <span className='badge badge-light-primary fs-8 fw-semibold'>
                          {[student.class, student.section].filter(Boolean).join(' ')}
                        </span>
                      )}
                    </span>
                    {student.subjects.length > 0 && (
                      <span className='d-block text-muted fs-7 mt-1'>
                        {student.subjects.join(' · ')}
                      </span>
                    )}
                  </div>
                ))}
                {more > 0 && (
                  <p className='text-muted fs-7 mb-0'>
                    {intl.formatMessage({id: 'DATA_UPLOAD.NEW_STUDENTS.MORE'}, {count: more})}
                  </p>
                )}
              </>
            )}
          </section>
        )}
      </Modal.Body>

      <Modal.Footer className='border-0 d-flex justify-content-end gap-2'>
        <button type='button' className='btn btn-light' disabled={submitting} onClick={onCancel}>
          {intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.DONT_SAVE'})}
        </button>
        <button
          type='button'
          className='btn btn-primary d-inline-flex align-items-center gap-2'
          disabled={submitting}
          onClick={() =>
            onConfirm({
              // Nobody to add: the teachers are matched or skipped, none saved.
              teachers: savesTeachers ? teacherChoice : 'none',
              teacherKeys: savesTeachers ? picked.map((teacher) => teacher.key) : [],
              teacherMatches: matches,
              students: hasStudents ? studentChoice : 'none',
            })
          }
        >
          {submitting && (
            <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
          )}
          {intl.formatMessage({id: 'DATA_UPLOAD.NEW_PEOPLE.CONTINUE'})}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default NewPeopleModal
